<template>
  <div class="pc">
    <div class="ec-header">
      <div><h2 class="ec-header__title">Plans &amp; Pricing</h2><p class="ec-header__subtitle">Manage plan names and numeric limits shown on both pricing pages.</p></div>
      <button type="button" class="ec-btn ec-btn--outline" :disabled="loading || saving" @click="reload">Reload saved</button>
    </div>
    <p class="pc-note">Text templates are fixed. Changing a number updates the cards, comparison table and decision guide together on the next page load. Saving also publishes the Starter name and limits for new registrations. Existing customer licences retain their current rights.</p>
    <p v-if="error" class="pc-error" role="alert">{{ error }}</p>
    <p v-if="message" class="pc-success" role="status">{{ message }}</p>
    <p v-if="loading" role="status">Loading pricing content…</p>
    <form v-else-if="content" novalidate @submit.prevent="save">
      <fieldset :disabled="saving">
        <section class="pc-card">
          <h3>Plan names</h3>
          <div class="pc-grid pc-grid--three">
            <label v-for="plan in plans" :key="plan.id">{{ plan.category }}
              <input v-model="content.planNames[plan.id]" required maxlength="100" :aria-label="plan.category + ' plan name'">
            </label>
          </div>
          <h3>{{ content.planNames.starter }} limits</h3>
          <div class="pc-grid">
            <label>Device limit<input v-model.number="content.starter.devices" type="number" min="1" max="1000000" step="1" required></label>
            <label>Camera stream limit<input v-model.number="content.starter.cameraStreams" type="number" min="1" max="1000000" step="1" required></label>
          </div>
          <h3>{{ content.planNames.growth }} limits &amp; price</h3>
          <div class="pc-grid pc-grid--three">
            <label>Device limit<input v-model.number="content.growth.devices" type="number" min="1" max="1000000" step="1" required></label>
            <label>Camera stream limit<input v-model.number="content.growth.cameraStreams" type="number" min="1" max="1000000" step="1" required></label>
            <label>Monthly $ / device / mo<input v-model.number="content.growth.pricePerDevice" type="number" min="0" max="1000000" step="1" required></label>
            <label>Yearly $ / device / yr (billed annually)<input v-model.number="content.growth.pricePerDeviceYearly" type="number" min="0" max="1000000" step="1" required></label>
          </div>
          <p class="pc-hint">Monthly price is per device per month; yearly price is per device per <strong>year</strong> (so round annual totals need only whole numbers — e.g. {{ content.growth.devices }} devices × $100/yr = ${{ content.growth.devices * 100 }}/yr). {{ content.planNames.growth }} card shows <strong>${{ growthMonthlyTotal }}/mo</strong> or <strong>${{ growthYearlyTotal }}/yr</strong> billed annually (≈ ${{ growthYearlyMonthlyEquiv }}/mo)<template v-if="growthSavePct">, saving <strong>{{ growthSavePct }}%</strong></template>. The discount is derived automatically.</p>
          <p v-if="growthSavePct === 0" class="pc-error">Set the yearly per-device price below the monthly rate annualised (monthly × 12) for it to read as a discount.</p>
          <p class="pc-hint">Enterprise and Partner names are display labels; their contracted licence rights are managed separately.</p>
        </section>
        <section class="pc-card pc-preview" aria-label="Live preview">
          <h3>Live preview</h3>
          <p class="pc-hint">The same numbers appear in the card and comparison table automatically.</p>
          <h4>{{ content.planNames.starter }} <span class="pc-hint">· Free</span></h4>
          <ul><li>Up to {{ content.starter.devices }} {{ content.starter.devices === 1 ? 'device' : 'devices' }}</li><li>{{ content.starter.cameraStreams }} camera {{ content.starter.cameraStreams === 1 ? 'stream' : 'streams' }} per device ({{ (content.starter.devices || 0) * (content.starter.cameraStreams || 0) }} total)</li></ul>
          <h4>{{ content.planNames.growth }} <span class="pc-hint">· ${{ growthMonthlyTotal }}/mo or ${{ growthYearlyTotal }}/yr (≈ ${{ growthYearlyMonthlyEquiv }}/mo) billed annually</span></h4>
          <ul><li>Up to {{ content.growth.devices }} {{ content.growth.devices === 1 ? 'device' : 'devices' }}</li><li>{{ content.growth.cameraStreams }} camera {{ content.growth.cameraStreams === 1 ? 'stream' : 'streams' }} per device ({{ (content.growth.devices || 0) * (content.growth.cameraStreams || 0) }} total)</li></ul>
          <div class="pc-table-scroll"><table><thead><tr><th scope="col">Side by side</th><th v-for="plan in plans" :key="plan.id" scope="col">{{ content.planNames[plan.id] }}</th></tr></thead>
            <tbody><tr><th scope="row">Devices</th><td>Up to {{ content.starter.devices }}</td><td>Up to {{ content.growth.devices }}</td><td>Sized to your fleet</td><td>Per partner bundle</td></tr><tr><th scope="row">Camera streams</th><td>{{ content.starter.cameraStreams }} per device ({{ (content.starter.devices || 0) * (content.starter.cameraStreams || 0) }} total)</td><td>{{ content.growth.cameraStreams }} per device ({{ (content.growth.devices || 0) * (content.growth.cameraStreams || 0) }} total)</td><td>Sized to your fleet</td><td>Per partner bundle</td></tr></tbody>
          </table></div>
        </section>
      </fieldset>
      <div class="pc-footer"><span class="pc-hint">{{ dirty ? 'You have unsaved changes.' : 'All changes saved.' }}</span><button type="submit" class="ec-btn ec-btn--primary" :disabled="saving || !dirty">{{ saving ? 'Saving…' : 'Save changes' }}</button></div>
    </form>
  </div>
</template>

<script>
import { apiService } from '../../service/api.service'
export default {
  name: 'PricingContentSettings',
  data: () => ({
    content: null,
    revision: 0,
    saved: '',
    loading: false,
    saving: false,
    error: '',
    message: '',
    plans: [{ id: 'starter', category: 'Self-serve' }, { id: 'growth', category: 'Self-serve' }, { id: 'enterprise', category: 'Sales-led' }, { id: 'partner', category: 'Channel' }]
  }),
  computed: {
    dirty () { return this.content && JSON.stringify(this.content) !== this.saved },
    growthMonthlyTotal () { const g = this.content && this.content.growth; return g ? (g.devices || 0) * (g.pricePerDevice || 0) : 0 },
    // pricePerDeviceYearly is $/device/YEAR, so devices × it is already the /yr total.
    growthYearlyTotal () { const g = this.content && this.content.growth; return g ? (g.devices || 0) * (g.pricePerDeviceYearly || 0) : 0 },
    growthYearlyMonthlyEquiv () { return Math.round(this.growthYearlyTotal / 12) },
    // Monthly is $/device/month, yearly is $/device/year, so annualise the monthly
    // rate (× 12) before comparing to derive the yearly saving.
    growthSavePct () {
      const g = this.content && this.content.growth
      if (!g) return 0
      const monthlyAnnualised = (g.pricePerDevice || 0) * 12
      return monthlyAnnualised && g.pricePerDeviceYearly < monthlyAnnualised
        ? Math.round((1 - g.pricePerDeviceYearly / monthlyAnnualised) * 100) : 0
    }
  },
  created () { this.load() },
  beforeRouteLeave (to, from, next) { next(!this.dirty || window.confirm('Discard unsaved pricing changes?')) },
  methods: {
    reload () { if (!this.dirty || window.confirm('Discard unsaved pricing changes and reload?')) this.load() },
    async load () {
      this.loading = true; this.error = ''; this.message = ''
      try {
        const { data } = await apiService.get('admin/pricing-content')
        this.content = data.data; this.revision = data.revision; this.saved = JSON.stringify(this.content)
      } catch (err) { this.error = err.response?.data?.message || 'Could not load pricing content. Use Reload saved to retry.' } finally { this.loading = false }
    },
    async save (event) {
      if (this.saving) return
      const invalid = event.target.querySelector('input:invalid, textarea:invalid')
      if (invalid) {
        invalid.reportValidity()
        return
      }
      this.saving = true; this.error = ''; this.message = ''
      try {
        const { data } = await apiService.put('admin/pricing-content', { data: this.content, revision: this.revision })
        this.content = data.data; this.revision = data.revision; this.saved = JSON.stringify(this.content)
        this.message = 'Saved. Pricing pages and new Starter registrations now use these values. Existing licences are unchanged.'
      } catch (err) { this.error = err.response?.data?.message || 'Could not save pricing content. Your changes are still here.' } finally { this.saving = false }
    }
  }
}
</script>

<style scoped>
.pc { font: 13px/1.5 var(--ec-font); color: var(--ec-text-1); }
.pc fieldset { border: 0; padding: 0; min-width: 0; }
.pc-note, .pc-error, .pc-success { padding: 12px 16px; border-radius: var(--ec-radius-sm); }
.pc-note { background: var(--ec-primary-bg); color: var(--ec-primary-dk); }
.pc-error { background: var(--ec-danger-bg); color: var(--ec-danger-dk); }
.pc-success { background: var(--ec-success-bg); color: var(--ec-success); }
.pc-card { padding: 20px; background: var(--ec-surface); border: 1px solid var(--ec-border); border-radius: var(--ec-radius); }
.pc h3 { font-size: 16px; margin-bottom: 16px; }
.pc h4 { font-size: 13px; margin: 16px 0 4px; }
.pc-hint { color: var(--ec-text-3); font-size: 12px; }
.pc label { display: block; font-size: 11px; font-weight: 600; color: var(--ec-text-2); margin-bottom: 12px; }
.pc input, .pc textarea { display: block; width: 100%; margin-top: 5px; padding: 8px 12px; border: 1px solid var(--ec-border); border-radius: var(--ec-radius-sm); background: var(--ec-surface); font: 13px/1.5 var(--ec-font); color: var(--ec-text-1); }
.pc input:focus, .pc textarea:focus { outline: 2px solid var(--ec-primary-ring); border-color: var(--ec-primary-fc); }
.pc button:focus-visible { outline: 2px solid var(--ec-primary-fc); outline-offset: 2px; }
.pc-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
.pc-grid--three { grid-template-columns: repeat(4, minmax(0, 1fr)); }
.pc-footer { position: sticky; bottom: 0; display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 16px 0; background: var(--ec-bg); }
@media (max-width: 700px) { .pc-grid, .pc-grid--three { grid-template-columns: 1fr; } }

.pc-preview { margin-top: 16px; }
.pc-preview ul { padding-left: 20px; }
.pc-table-scroll { overflow-x: auto; }
.pc-preview table { width: 100%; min-width: 560px; border-collapse: collapse; margin-top: 16px; text-align: left; }
.pc-preview th, .pc-preview td { padding: 10px 12px; border-bottom: 1px solid var(--ec-border); }
</style>
